import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Plus, Loader2, Ship, Search, ChevronRight } from "lucide-react";
import { api, formatApiError } from "@/api/client";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { usePipelineMeta } from "@/lib/pipeline";

export function VesselFormDialog({ open, onOpenChange, vessel, onSaved }) {
  const [form, setForm] = useState({ vessel_name: "", owner_name: "", imo_number: "", vessel_type: "" });
  const [saving, setSaving] = useState(false);
  const meta = usePipelineMeta();

  useEffect(() => {
    if (open) {
      setForm({
        vessel_name: vessel?.vessel_name || "",
        owner_name: vessel?.owner_name || "",
        imo_number: vessel?.imo_number || "",
        vessel_type: vessel?.vessel_type || "",
      });
    }
  }, [open, vessel]);

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const body = { ...form, vessel_type: form.vessel_type || null };
      if (vessel) await api.patch(`/vessels/${vessel.id}`, body);
      else await api.post("/vessels", body);
      toast.success(vessel ? "Vessel updated" : "Vessel added");
      onOpenChange(false);
      onSaved();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSaving(false);
    }
  };

  const field = (key, label, placeholder, required = true) => (
    <div className="space-y-1.5">
      <Label htmlFor={key}>{label}{!required && <span className="text-slate-400 font-normal"> (optional)</span>}</Label>
      <Input id={key} value={form[key]} required={required} placeholder={placeholder}
        onChange={(e) => setForm({ ...form, [key]: e.target.value })} data-testid={`vessel-form-${key}-input`} />
    </div>
  );

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="vessel-form-dialog">
        <DialogHeader>
          <DialogTitle>{vessel ? "Edit vessel" : "Add vessel"}</DialogTitle>
          <DialogDescription>Vessel names must be unique.</DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-4">
          {field("vessel_name", "Vessel name", "e.g. MV Ocean Pride")}
          {field("owner_name", "Owner name", "e.g. Anglo Eastern")}
          {field("imo_number", "IMO number", "e.g. 9321483", false)}
          <div className="space-y-1.5">
            <Label>Vessel type <span className="text-slate-400 font-normal">(optional)</span></Label>
            <Select value={form.vessel_type || "none"} onValueChange={(v) => setForm({ ...form, vessel_type: v === "none" ? "" : v })}>
              <SelectTrigger data-testid="vessel-form-vessel_type-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="none">Not set</SelectItem>
                {(meta?.vessel_types || []).map((t) => <SelectItem key={t} value={t} data-testid={`vessel-type-option-${t.replace(/[^a-z0-9]+/gi, "-").toLowerCase()}`}>{t}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <DialogFooter>
            <Button type="submit" disabled={saving} className="bg-[#0b192c] hover:bg-[#0f2544]" data-testid="vessel-form-submit-btn">
              {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : vessel ? "Save changes" : "Add vessel"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function VesselRow({ v, onOpen, counts }) {
  return (
    <tr className="border-t border-slate-100 hover:bg-slate-50/70 transition-colors" data-testid={`vessel-row-${v.id}`}>
      <td className="px-5 py-3.5">
        <div className="font-semibold text-slate-800" data-testid={`vessel-name-${v.id}`}>{v.vessel_name}</div>
        {v.imo_number && <div className="text-xs text-slate-400 font-mono mt-0.5">IMO {v.imo_number}</div>}
        {v.vessel_type && <div className="text-xs text-cyan-700 mt-0.5" data-testid={`vessel-type-${v.id}`}>{v.vessel_type}</div>}
      </td>
      <td className="px-5 py-3.5 text-slate-600" data-testid={`vessel-owner-${v.id}`}>{v.owner_name}</td>
      <td className="px-5 py-3.5">
        <span className="font-mono font-semibold text-slate-800" data-testid={`vessel-rank-count-${v.id}`}>{v.rank_count}</span>
        <span className="text-xs text-slate-400 ml-1.5">ranks · {v.total_required} crew</span>
      </td>
      <td className="px-5 py-3.5 hidden md:table-cell" data-testid={`vessel-pipeline-counts-${v.id}`}>
        <span className="font-mono font-semibold text-sky-700" data-testid={`vessel-open-reqs-${v.id}`}>{counts?.open_requirements || 0}</span>
        <span className="text-xs text-slate-400 ml-1">open</span>
        <span className="font-mono font-semibold text-indigo-700 ml-3" data-testid={`vessel-pipeline-${v.id}`}>{counts?.pipeline || 0}</span>
        <span className="text-xs text-slate-400 ml-1">in pipeline</span>
      </td>
      <td className="px-5 py-3.5 text-right">
        <Button size="sm" variant="outline" onClick={onOpen} data-testid={`vessel-view-btn-${v.id}`}>
          View <ChevronRight className="w-3.5 h-3.5 ml-1" />
        </Button>
      </td>
    </tr>
  );
}

export default function VesselsPage() {
  const { hasPermission } = useAuth();
  const canManage = hasPermission("vessels.manage");
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [counts, setCounts] = useState({});
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [addOpen, setAddOpen] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const [{ data }, c] = await Promise.all([
        api.get("/vessels", { params: search ? { search } : {} }),
        api.get("/pipeline/vessel-counts").catch(() => ({ data: {} })),
      ]);
      setItems(data);
      setCounts(c.data);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const t = setTimeout(load, 250);
    return () => clearTimeout(t);
    // eslint-disable-next-line
  }, [search]);

  return (
    <div className="space-y-6" data-testid="vessels-page">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <div className="text-xs uppercase tracking-widest text-slate-400">Fleet</div>
          <h1 className="text-3xl font-bold text-slate-900 tracking-tight mt-1" style={{ fontFamily: "Manrope" }}>Vessels</h1>
          <div className="text-sm text-slate-500 mt-1">
            {canManage ? "Every vessel and the ranks it needs filled." : "Vessels with ranks assigned to you."}
          </div>
        </div>
        {canManage && (
          <Button onClick={() => setAddOpen(true)} className="bg-[#0b192c] hover:bg-[#0f2544]" data-testid="add-vessel-btn">
            <Plus className="w-4 h-4 mr-1" /> Add vessel
          </Button>
        )}
      </div>

      <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
        <div className="p-4 border-b border-slate-100">
          <div className="relative max-w-sm">
            <Search className="w-4 h-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
            <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search vessel, owner or IMO"
              className="pl-9" data-testid="vessel-search-input" />
          </div>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="vessels-table">
            <thead className="bg-slate-50 text-xs uppercase tracking-wider text-slate-500">
              <tr>
                <th className="px-5 py-3 text-left font-medium">Vessel</th>
                <th className="px-5 py-3 text-left font-medium">Owner</th>
                <th className="px-5 py-3 text-left font-medium">Ranks</th>
                <th className="px-5 py-3 text-left font-medium hidden md:table-cell">Crew Requirements / Pipeline</th>
                <th className="px-5 py-3"></th>
              </tr>
            </thead>
            <tbody>
              {!loading && items.map((v) => <VesselRow key={v.id} v={v} counts={counts[v.id]} onOpen={() => navigate(`/vessels/${v.id}`)} />)}
            </tbody>
          </table>
        </div>
        {loading && <div className="py-12 text-center"><Loader2 className="w-5 h-5 animate-spin text-slate-400 inline" /></div>}
        {!loading && items.length === 0 && (
          <div className="py-14 text-center" data-testid="vessels-empty-state">
            <Ship className="w-8 h-8 text-slate-300 mx-auto" />
            <div className="text-sm text-slate-500 mt-2">
              {search ? "No vessels match your search" : canManage ? "No vessels yet — add your first one." : "No ranks are assigned to you yet."}
            </div>
          </div>
        )}
      </div>

      <VesselFormDialog open={addOpen} onOpenChange={setAddOpen} onSaved={load} />
    </div>
  );
}
